import { extend, override } from 'flarum/common/extend';
/**
 * BlueEmotion · fof/polls customization.
 *
 * A monkey-patch layer over the third-party fof/polls extension. We never fork
 * polls — we patch its prototypes and reshape behavior + markup to fit the
 * brand. Three independent patches:
 *
 *   1. Submit UI — voting becomes review-then-commit instead of
 *      click-and-submit (forced `useSubmitUI` + always-on `showButton`).
 *   2. Subtitle markdown links — render `[label](url)` in a poll subtitle as a
 *      real anchor.
 *   3. Image + subview layout — hoist a poll's image out of the content body
 *      and wrap the rest in a `.Poll-subview` so the two sit side by side.
 *
 * Options render with fof/polls's own PollOption (a real radio/checkbox inside
 * a <label>); Polls.less restyles it. Remaining brand polish (pill shape, bar
 * gradient fills, checkbox, Submit button styling, the .Poll-subview grid)
 * lives there too.
 *
 * Targets are passed to extend()/override() by `ext:` string path, so each
 * patch applies whenever fof/polls registers the module — independent of
 * bundle load order, and a no-op when polls isn't installed (it's an optional
 * dependency).
 *
 * Patches 2 and 3 target AbstractPoll so they cover both PollView (poll pages)
 * and PostPoll (polls embedded in posts).
 */
function escapeHtml(text) {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
export default function customizePoll() {
  // --- 1. Submit UI -------------------------------------------------------
  // `useSubmitUI` is a prototype getter with no setter, so assigning it throws;
  // shadow it with an own property on the instance instead.
  extend('ext:fof/polls/forum/states/PollState', 'init', function () {
    Object.defineProperty(this, 'useSubmitUI', {
      value: true
    });
  });

  // fof/polls's native showButton is `useSubmitUI && pendingSubmit`, so the
  // button only appears AFTER a selection is staged. We want it
  // visible-but-disabled whenever the viewer can pick (the native
  // PollSubmitButton is already `disabled: !hasSelectedOptions()`), including
  // after voting on a poll that allows changing the vote. canSelect() is false
  // once the poll has ended, when the viewer can't vote, or after a vote on a
  // poll that forbids changes. `loadingOptions` keeps it mounted (in its
  // loading state) while the vote request is in flight, when canSelect() is
  // briefly false.
  override('ext:fof/polls/forum/states/PollState', 'showButton', function () {
    return this.useSubmitUI && (this.canSelect() || this.loadingOptions);
  });

  // --- 2. Markdown links in the subtitle ----------------------------------
  extend('ext:fof/polls/forum/components/Poll/AbstractPoll', 'headerItems', function (items) {
    var _poll$translation$sub, _poll$translation, _poll$translation$sub2;
    if (!items.has('subtitle')) return;
    const poll = this.attrs.poll;
    const subtitle = (_poll$translation$sub = (_poll$translation = poll.translation) == null || (_poll$translation = _poll$translation.call(poll)) == null || (_poll$translation$sub2 = _poll$translation.subtitle) == null ? void 0 : _poll$translation$sub2.call(_poll$translation)) != null ? _poll$translation$sub : poll.subtitle();
    if (!subtitle) return;

    // Escape first: the subtitle is user input and ends up in m.trust().
    // Only http(s) targets become links, so `javascript:` can't sneak in.
    const html = escapeHtml(subtitle).replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="nofollow noopener ugc">$1</a>');
    items.setContent('subtitle', m("p", {
      className: "Poll-subtitle helpText"
    }, m.trust(html)));
  });

  // --- 3. Image + subview layout ------------------------------------------
  // contentItems adds an 'image' item when the poll has one. Pull it out and
  // stash it on the instance so viewItems can re-place it.
  extend('ext:fof/polls/forum/components/Poll/AbstractPoll', 'contentItems', function (items) {
    // @ts-ignore — instance scratch field
    this.___image = items.has('image') ? items.get('image') : null;
    if (items.has('image')) items.remove('image');
  });

  // Rebuild the poll as [ image, .Poll-subview(header/content/footer) ].
  // viewItems() calls contentItems() itself, so the stash above is fresh.
  // NOTE: extend() discards the callback's return value — the ORIGINAL
  // ItemList is what gets rendered. So we mutate `items` IN PLACE (clear its
  // keys, then re-add) rather than returning a new list.
  extend('ext:fof/polls/forum/components/Poll/AbstractPoll', 'viewItems', function (items) {
    // @ts-ignore — set by the contentItems patch above
    const image = this.___image;
    if (!image) return;
    const inner = items.toArray();
    Object.keys(items.toObject()).forEach(key => items.remove(key));
    items.add('image', image, 100);
    items.add('subview', m("div", {
      class: "Poll-subview"
    }, inner), 90);
  });
}
flarum.reg.add('glowingblue-demo', 'forum/extenders/customizePoll', customizePoll);